import React, { useEffect, useRef, useState } from 'react'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import {
  Btn,
  CustomCalendar,
  CustomDropdown,
  CustomMultiSelect,
  CustomPanel,
  PageHeader,
} from '../../../../components'
import { Fieldset } from 'primereact/fieldset'
import { FaUpload } from 'react-icons/fa'
import {
  hideLoadingBox,
  showLoadingBox,
  showMessageBox,
} from '../../../../store/actions/system.actions'
import { errorHandler } from '../../../../services/util/error.service'
import {
  getRelacaoEntregasCanhoto,
  getResumoEntregasCanhoto,
} from '../../../../services/api/https/deliveries/delivery.http'
import { parseToMysqlFormat } from '../../../../services/util/date.service'
import { getEmpresas } from '../../../../services/localstorage/localstorage.service'
import { useGestaoCanhotoContext } from '../../../../contexts/EntregaContext'
import CanhotoCards from './CanhotoCards'
import EntregasCanhotoDataTable from './EntregasCanhotoDataTable'

const TEM_CANHOTO = 0

const mysqlToJsDate = value => {
  const [year, month, day] = value.split('-')
  return new Date(year, month - 1, day)
}

function EntregaSemCanhoto({ history, dispatch, selectedTp }) {
  // Vars
  const {
    location: {
      state: { transportadora_id, dataInicial, dataFinal, transportadora, company_id },
    },
  } = history
  const context = useGestaoCanhotoContext()
  const empresasOptions = getEmpresas()

  // Hooks
  const [empresa, setEmpresa] = useState(empresasOptions.find(e => e.id === company_id) || null)
  const [pInicial, setPeriodoInicial] = useState(mysqlToJsDate(dataInicial))
  const [pFinal, setPeriodoFinal] = useState(mysqlToJsDate(dataFinal))
  const [filtrosAtivos, setFiltrosAtivos] = useState(null)
  const [resumo, setResumo] = useState(null)
  const [entregas, setEntregas] = useState([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [first, setFirst] = useState(0)
  const [rows, setRows] = useState(20)
  const [loadingTable, setLoadingTable] = useState(false)
  const [statusOptions, setStatusOptions] = useState([])
  const [statusSelecionados, setStatusSelecionados] = useState([])
  const [search, setSearch] = useState('')
  const searchTimer = useRef(null)

  // Helpers
  const montarFiltros = (empresa, pInicial, pFinal) => ({
    dataInicial: parseToMysqlFormat(pInicial),
    dataFinal: parseToMysqlFormat(pFinal),
    transportadoraId: transportadora_id || '',
    companyId: empresa.id,
  })

  const fetchPagina = async (filtros, { first, rows, statusSel, searchTerm }) => {
    try {
      setLoadingTable(true)
      const retorno = await getRelacaoEntregasCanhoto(selectedTp, {
        ...filtros,
        temCanhoto: TEM_CANHOTO,
        status: statusSel.join(','),
        search: searchTerm,
        page: Math.floor(first / rows),
        pageSize: rows,
      })
      setEntregas(retorno.rows)
      setTotalRecords(retorno.total)
      setFirst(first)
      setRows(rows)
    } catch (e) {
      dispatch(showMessageBox(null, errorHandler(e)))
    } finally {
      setLoadingTable(false)
    }
  }

  const fetchEntregas = async (empresa, pInicial, pFinal) => {
    try {
      if (selectedTp === 0) throw new Error('Selecione o Operador Logístico')
      if (!empresa) throw new Error('Selecione a Empresa')
      dispatch(showLoadingBox('Carregando as entregas sem canhoto', 'Aguarde . . .'))

      const filtros = montarFiltros(empresa, pInicial, pFinal)
      const [dadosResumo] = await Promise.all([
        getResumoEntregasCanhoto(selectedTp, filtros),
        fetchPagina(filtros, { first: 0, rows, statusSel: [], searchTerm: '' }),
      ])

      setResumo(dadosResumo)
      setStatusOptions(
        dadosResumo.status_list
          .filter(s => !s.tem_canhoto)
          .map(s => ({ label: s.status, value: s.status })),
      )
      setStatusSelecionados([])
      setSearch('')
      setFiltrosAtivos(filtros)
    } catch (e) {
      dispatch(showMessageBox(null, errorHandler(e)))
    } finally {
      dispatch(hideLoadingBox())
    }
  }

  useEffect(() => {
    if (empresa) fetchEntregas(empresa, pInicial, pFinal)
  }, [])

  const onPeriodoChange = (campo, value) => {
    context.setGestaoCanhotoContext(prev => ({
      ...prev,
      selectedPeriodo: { ...prev.selectedPeriodo, [campo]: value },
    }))
    if (campo === 'dataInicial') setPeriodoInicial(value)
    else setPeriodoFinal(value)
  }

  const onStatusChange = value => {
    setStatusSelecionados(value)
    if (filtrosAtivos) {
      fetchPagina(filtrosAtivos, { first: 0, rows, statusSel: value, searchTerm: search })
    }
  }

  const onSearchChange = value => {
    setSearch(value)
    if (!filtrosAtivos) return
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => {
      fetchPagina(filtrosAtivos, {
        first: 0,
        rows,
        statusSel: statusSelecionados,
        searchTerm: value,
      })
    }, 600)
  }

  const onPage = e => {
    fetchPagina(filtrosAtivos, {
      first: e.first,
      rows: e.rows,
      statusSel: statusSelecionados,
      searchTerm: search,
    })
  }

  const onClickUpload = deliveryId => {
    history.push('/main/entregas/anexar-canhoto', { delivery_id: deliveryId })
  }

  // Render
  return (
    <React.Fragment>
      <PageHeader>
        3.17 - Gestão de Canhotos – Entregas sem Canhoto –<strong> {transportadora}</strong>
      </PageHeader>

      <div style={{ marginTop: 20 }}>
        <CustomPanel showBtnNovo={false} onVoltar={history.goBack}>
          <div className="p-grid">
            <div className="p-col-3">
              <CustomDropdown
                label="Empresa"
                optionLabel="name"
                options={empresasOptions}
                value={empresa}
                onChange={setEmpresa}
                placeholder="Selecione a empresa"
                filter={true}
              />
            </div>
            <div className="p-col-9" style={{ display: 'flex' }}>
              <CustomCalendar
                label="Período Inicial"
                value={pInicial}
                onChange={value => onPeriodoChange('dataInicial', value)}
              />
              <CustomCalendar
                label="Período Final"
                value={pFinal}
                onChange={value => onPeriodoChange('dataFinal', value)}
              />
              <div style={{ marginLeft: 10, paddingTop: 27 }}>
                <Btn onClick={() => fetchEntregas(empresa, pInicial, pFinal)}>Pesquisar</Btn>
              </div>
            </div>
          </div>

          <CanhotoCards resumo={resumo} />

          <Fieldset legend="Filtro" toggleable collapsed={true} className="p-col-12">
            <div className="p-grid p-align-end">
              <div className="p-col-3">
                <CustomMultiSelect
                  label="Status"
                  options={statusOptions}
                  value={statusSelecionados}
                  onChange={onStatusChange}
                  filterPlaceholder="Buscar status..."
                />
              </div>
            </div>
          </Fieldset>

          <div style={{ marginTop: 15 }}>
            <EntregasCanhotoDataTable
              entregas={entregas}
              totalRecords={totalRecords}
              first={first}
              rows={rows}
              loading={loadingTable}
              onPage={onPage}
              search={search}
              onSearchChange={onSearchChange}
              acaoIcon={<FaUpload />}
              onClickAcao={onClickUpload}
            />
          </div>
        </CustomPanel>
      </div>
    </React.Fragment>
  )
}

const mapStateToProps = ({ system: { selectedTp } }) => ({ selectedTp })
export default connect(mapStateToProps)(withRouter(EntregaSemCanhoto))
